import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { classifySeoRoute } from "./classifySeoRoute";
import { loadRuntimeSeoRules } from "./loadRuntimeSeoRules";
import { loadAgentSeoOverride } from "./loadAgentSeoOverride";

import { resolvePublicBaseUrl } from "../utils/resolvePublicBaseUrl";
import { parseLocaleFromPathname } from "../utils/localeUrl";
import { resolvePublicB2cSeoDefaults } from "./publicB2cSeoDefaults";
import { isTripMasterSurface } from "../surfaces/tripMaster/tripMasterEnv";
import { buildTripMasterHomeTitle } from "../surfaces/tripMaster/tripMasterDocumentTitle";

export const BASE_TITLE = resolvePublicB2cSeoDefaults().title;

const buildAbsoluteCanonical = (canonicalPath, origin) => {
  if (!canonicalPath) {
    return null;
  }
  const baseOrigin =
    origin ||
    (typeof window !== "undefined" ? resolvePublicBaseUrl({ skipTrace: true }) : undefined);
  if (!baseOrigin) {
    return canonicalPath;
  }
  return new URL(canonicalPath, baseOrigin).toString();
};

const buildAbsolutePageUrl = (pathname, search, origin) => {
  const baseOrigin =
    origin ||
    (typeof window !== "undefined" ? resolvePublicBaseUrl({ skipTrace: true }) : undefined);
  if (!baseOrigin || typeof pathname !== "string") return null;
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const qs = String(search || "").replace(/^\?/, "").trim();
  return qs ? `${baseOrigin}${path}?${qs}` : `${baseOrigin}${path}`;
};

export default function SeoMetaGuard({
  agentSeoOverride,
  siteOrigin,
  agentId,
  siteSettings,
  publicSiteTitle,
  publicSiteDescription,
  publicSiteKeywords,
  isPublicAgentSite = false,
  isPublicAgentSitePending = false,
}) {
  const location = useLocation();
  const [runtimeRules, setRuntimeRules] = useState(null);
  const [runtimeAgentOverride, setRuntimeAgentOverride] = useState(null);

  useEffect(() => {
    let mounted = true;
    loadRuntimeSeoRules()
      .then((rules) => {
        if (!mounted) {
          return;
        }
        setRuntimeRules(rules || null);
      })
      .catch(() => {
        if (!mounted) {
          return;
        }
        setRuntimeRules(null);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    if (agentId === null || agentId === undefined || agentId === "") {
      setRuntimeAgentOverride(null);
      return () => {
        mounted = false;
      };
    }

    loadAgentSeoOverride({
      agentId,
      pathname: location.pathname,
    })
      .then((override) => {
        if (!mounted) {
          return;
        }
        setRuntimeAgentOverride(override || null);
      })
      .catch(() => {
        if (!mounted) {
          return;
        }
        setRuntimeAgentOverride(null);
      });

    return () => {
      mounted = false;
    };
  }, [agentId, location.pathname]);

  const effectiveAgentOverride =
    agentId === null || agentId === undefined || agentId === ""
      ? agentSeoOverride
      : runtimeAgentOverride;

  const classification = classifySeoRoute({
    pathname: location.pathname,
    searchParams: location.search,
    agentSeoOverride: effectiveAgentOverride,
    origin: siteOrigin,
    runtimeRules,
  });

  const canonicalHref = buildAbsoluteCanonical(classification.canonicalUrl, siteOrigin);
  const pageUrl = buildAbsolutePageUrl(location.pathname, location.search, siteOrigin);
  const hasOverrideTitle =
    typeof classification.title === "string" && classification.title.trim().length > 0;
  const hasOverrideDescription =
    typeof classification.description === "string" &&
    classification.description.trim().length > 0;
  const hasSiteSettingsTitle =
    typeof siteSettings?.meta_title === "string" &&
    siteSettings.meta_title.trim().length > 0;
  const hasSiteSettingsDescription =
    typeof siteSettings?.meta_description === "string" &&
    siteSettings.meta_description.trim().length > 0;
  const hasSiteSettingsKeywords =
    typeof siteSettings?.meta_keywords === "string" &&
    siteSettings.meta_keywords.trim().length > 0;
  const hasPublicSiteTitle =
    typeof publicSiteTitle === "string" && publicSiteTitle.trim().length > 0;
  const hasPublicSiteDescription =
    typeof publicSiteDescription === "string" && publicSiteDescription.trim().length > 0;
  const hasPublicSiteKeywords =
    typeof publicSiteKeywords === "string" && publicSiteKeywords.trim().length > 0;

  const effectiveTitle = hasOverrideTitle
    ? classification.title
    : hasSiteSettingsTitle
      ? siteSettings.meta_title
      : hasPublicSiteTitle
        ? publicSiteTitle.trim()
        : null;
  const effectiveDescription = hasOverrideDescription
    ? classification.description
    : hasSiteSettingsDescription
      ? siteSettings.meta_description
      : hasPublicSiteDescription
        ? publicSiteDescription.trim()
        : null;
  const effectiveKeywords = hasSiteSettingsKeywords
    ? siteSettings.meta_keywords
    : hasPublicSiteKeywords
      ? publicSiteKeywords.trim()
      : null;

  const { pathnameWithoutLocale } = parseLocaleFromPathname(location.pathname || "");
  const isPublicPackageViewRoute =
    /\/vacation-packages\/package\/view(?:\/|$)/i.test(pathnameWithoutLocale) ||
    /\/package\/view(?:\/|$)/i.test(pathnameWithoutLocale);

  if (isPublicPackageViewRoute) {
    return null;
  }

  const resolvedTitle = effectiveTitle;
  // TripMaster sets its own contextual <title> (search/package/home); never flash HotelsRiver SEO.
  const suppressHotelsRiverFallback =
    isPublicAgentSite || isPublicAgentSitePending || isTripMasterSurface();
  const shouldInjectBaseTitle = !resolvedTitle && !suppressHotelsRiverFallback;
  const shouldOverrideIndexTitle = Boolean(resolvedTitle) || suppressHotelsRiverFallback;
  const titleForHelmet =
    resolvedTitle ||
    (isTripMasterSurface()
      ? buildTripMasterHomeTitle()
      : suppressHotelsRiverFallback
        ? "\u200B"
        : null);

  return (
    <Helmet>
      <meta name="robots" content={classification.robotsContent} />
      {canonicalHref ? <link rel="canonical" href={canonicalHref} /> : null}
      {shouldOverrideIndexTitle && titleForHelmet != null ? (
        <title>{titleForHelmet}</title>
      ) : null}
      {effectiveDescription ? (
        <meta name="description" content={effectiveDescription} />
      ) : null}
      {effectiveKeywords ? <meta name="keywords" content={effectiveKeywords} /> : null}
      {resolvedTitle && pageUrl ? <meta property="og:url" content={pageUrl} /> : null}
      {resolvedTitle ? <meta property="og:type" content="website" /> : null}
      {resolvedTitle ? <meta property="og:title" content={resolvedTitle} /> : null}
      {effectiveDescription ? (
        <meta property="og:description" content={effectiveDescription} />
      ) : null}
      {resolvedTitle ? <meta property="og:site_name" content={resolvedTitle} /> : null}
      {resolvedTitle ? <meta name="twitter:card" content="summary" /> : null}
      {resolvedTitle ? <meta name="twitter:title" content={resolvedTitle} /> : null}
      {effectiveDescription ? (
        <meta name="twitter:description" content={effectiveDescription} />
      ) : null}
      {shouldInjectBaseTitle ? <title>{BASE_TITLE}</title> : null}
    </Helmet>
  );
}
